import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { DistributionManager } from '../../APIManager'
import { notification } from '../../components/Notifications'
import { type ApiError } from '../../models/Common'
import { grey } from '../../colors'

export interface SandboxModeModalProps {
  /** The Survey Project instance — the term — this rehearsal belongs to. */
  slotId: string
  /** Its own title, shown so the admin can see which term they are switching. */
  slotTitle?: string
  canEdit: boolean
  isOpen: boolean
  handleCloseModal: () => void
}

/**
 * The key `ProjectCourseFeature` already owns for this slot, and therefore its response shape too.
 * Query-core installs the fetching observer's own `queryFn` on the shared query, so a second
 * shape on this key overwrites the cache the page reads: it stores `{ data, permissions }` and
 * reads `slot.data`, and a bare slot here left the page's status, title, permissions and every
 * action button undefined behind the open modal. Found by review, 2026-09-20.
 */
const slotKey = (id: string): unknown[] => ['slotDataById', id]

/**
 * Sandbox is per **term**, not per Survey Project (backend `80a0e6006`, 2026-09-19). It was a
 * series column until then, which the backend's own review found to be worse than a naming
 * mistake: the switchable gate asked about every slot in the series and slots stay Closed once
 * they have run, so a single completed term blocked sandbox on that project for good; and
 * switching it on deleted every pending scheduled row for every slot, so rehearsing one term
 * dropped another's queued Live and Close transitions.
 */
const SandboxModeModal: React.FC<SandboxModeModalProps> = ({
  slotId,
  slotTitle,
  canEdit,
  isOpen,
  handleCloseModal
}) => {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(false)

  const { data: slotResponse, isLoading, isFetching, isError } = useQuery({
    queryKey: slotKey(slotId),
    queryFn: async () => {
      const response = await DistributionManager.getSlot(slotId)
      return response?.data
    },
    enabled: isOpen && slotId !== ''
  })
  const slotData = slotResponse?.data ?? slotResponse

  /**
   * Seeded once per opening, from a settled read.
   *
   * Not on every change of `isSandbox`: a background refetch — this key is shared with the page
   * behind the modal and `staleTime` is 0 — would otherwise discard the user's unsaved toggle
   * under their cursor. And not before the read settles, so the switch never shows a guess.
   * The sibling instructor-dates modal carries the same two guards for the same reasons.
   */
  const hasSeededRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      hasSeededRef.current = false
      return
    }
    // `isError` gates the seed, not just Save. Both loading flags go false on a failed GET too, so
    // without it the switch took Off from `undefined` and locked it — and `canSave` stops blocking
    // the moment a later refetch succeeds, handing back a Save that would PATCH `isSandbox: false`
    // over a rehearsal the server still calls sandboxed. Left unseeded, that refetch seeds.
    if (hasSeededRef.current || isLoading || isFetching || isError) return
    hasSeededRef.current = true
    setEnabled(slotData?.isSandbox === true)
  }, [isOpen, isLoading, isFetching, isError, slotData?.isSandbox])

  const { mutate: saveSetting, isPending: isSaving } = useMutation({
    mutationFn: async (nextEnabled: boolean) => {
      return await DistributionManager.updateDistributionPlan(slotId, {
        isSandbox: nextEnabled
      })
    },
    onSuccess: async (_data, nextEnabled) => {
      // Named, and in terms of what changed for people rather than which flag moved. "Saved", or
      // "Sandbox is on", tells an admin nothing about what they have just done: narrowed this
      // term's whole audience, and taken its results out of every real figure. The title leads
      // with who is affected rather than with the word "sandbox", which an admin meeting this
      // setting for the first time has no reason to know; the setting is named in the detail
      // line, where it reads as an instruction rather than as jargon.
      const termName = slotTitle ?? 'this Survey Project'
      notification(
        nextEnabled
          ? `Only system-level users can see ${termName} now`
          : `${termName} is visible to its audience again`,
        {
          variant: 'success',
          subTitle: nextEnabled
            ? 'It runs its schedule normally, but only system-level users receive its emails, and its results stay out of your reports.'
            : 'Its emails now reach everyone they are addressed to, and its results count towards your reports.'
        }
      )
      await queryClient.invalidateQueries({ queryKey: slotKey(slotId) })
      // The terms table carries the flag per row now, and a project whose every term is
      // sandboxed drops out of the project lists entirely — so both are refreshed rather than
      // left until a reload.
      await queryClient.invalidateQueries({ queryKey: ['distributionPlan'] })
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      handleCloseModal()
    },
    onError: (error: ApiError) => {
      notification(error?.message ?? 'Failed to save setting. Please try again.', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const loading = isLoading || isFetching
  /**
   * A failed read is not a value. `isLoading`/`isFetching` both go false when the GET errors, and
   * the switch then reads Off from `undefined` — so Save on an already-sandboxed term would PATCH
   * `isSandbox: false`, exposing a rehearsal to its instructors and students and resuming its
   * email. The sibling modal documents this exact failure; the guard was not carried over until
   * review found it, 2026-09-20.
   */
  const canSave = canEdit && !loading && !isError

  const handleSave = (): void => {
    if (!canSave) return
    saveSetting(enabled)
  }

  return (
    <GenericModal
      isOpen={isOpen}
      title="Sandbox Mode"
      onClose={handleCloseModal}
      maxWidth="sm"
      action={false}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {slotTitle !== undefined && slotTitle !== '' && (
          <Typography variant="body2" color={grey[700]}>
            Survey Project: <strong>{slotTitle}</strong>
          </Typography>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(_event, checked) => { setEnabled(checked) }}
              disabled={!canSave || isSaving}
            />
          }
          label="Keep this Survey Project in sandbox"
        />

        <Alert severity="info">
          <Typography variant="body2" mb={1}>
            While in sandbox, only users with a system-level role can see this Survey Project.
            Owners and Editors scoped to its Academic Units, instructors assigned to it, and
            students in its sections will not see it in lists or by direct link. Other terms of
            the same project are unaffected — sandbox applies to this one.
          </Typography>
          <Typography variant="body2" mb={1}>
            It runs its schedule normally — it opens, sends its emails, collects answers, closes
            and produces reports. Every email goes only to recipients who hold a system-level
            role, so a rehearsal never lands in an ordinary inbox.
          </Typography>
          <Typography variant="body2" mb={1}>
            Its responses stay out of your real figures: completion rates, Academic Unit rollups,
            term summaries and exports all leave a sandboxed term out.
          </Typography>
          <Typography variant="body2">
            Sandbox can only be turned on or off before the term opens. Once it has opened, a
            rehearsal stays a rehearsal — which is what keeps its answers from ever being counted
            as real.
          </Typography>
        </Alert>

        <DefaultModalActions
          confirmButtonProps={{
            text: 'Save',
            onClick: handleSave,
            loading: isSaving,
            disabled: !canSave
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal,
            disabled: isSaving
          }}
        />
      </Box>
    </GenericModal>
  )
}

export default SandboxModeModal
